import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Mail, Lock, ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { PasswordInput } from '../components/ui/password-input';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '', otp: '' });
  const [twofa, setTwofa] = useState(false);

  useEffect(() => {
    const savedEmail = window.localStorage.getItem('n369_last_login_email');
    if (savedEmail) setForm((prev) => ({ ...prev, email: savedEmail }));
  }, []);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const accessMode = location.pathname.includes('acceso-admin')
    ? 'admin'
    : location.pathname.includes('acceso-partners')
      ? 'partners'
      : location.pathname.includes('acceso-agencias')
        ? 'agencias'
      : location.pathname.includes('acceso-clinicas')
        ? 'clinicas'
      : location.pathname.includes('acceso-pacientes')
        ? 'pacientes'
      : location.pathname.includes('acceso-mlm')
        ? 'mlm'
        : 'general';

  const content = {
    mlm: {
      title: 'Acceso FitLine · PM International',
      subtitle: 'Tu negocio en automático con Aynara',
      hint: 'Panel privado para gestionar prospectos, redes, email, ventas y equipo. Habla con Aynara por voz.'
    },
    admin: {
      title: 'Acceso Administración',
      subtitle: 'Centro de control Neuralia369',
      hint: 'Acceso reservado para el equipo administrador.'
    },
    partners: {
      title: 'Acceso Partners',
      subtitle: 'Portal privado para partners y asociados',
      hint: 'Consulta tu panel privado, agentes asignados y condiciones especiales.'
    },
    agencias: {
      title: 'Acceso Agencias',
      subtitle: 'Portal comercial para agencias',
      hint: 'Accede a materiales, pricing y control comercial de agencia.'
    },
    clinicas: {
      title: 'Acceso Clínicas',
      subtitle: 'Operación clínica Dra. Sandra Alvarez',
      hint: 'Acceso directo al panel clínico real por roles, agenda, stock e historial en evolución.'
    },
    pacientes: {
      title: 'Acceso Pacientes',
      subtitle: 'Portal paciente Neuralia369',
      hint: 'Acceso preparado para mostrar la experiencia del paciente, seguimiento y portal privado.'
    },
    general: {
      title: 'Acceso al Panel',
      subtitle: 'Neuralia369',
      hint: 'El acceso es gestionado por el administrador.'
    }
  }[accessMode];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password, form.otp);
      window.localStorage.setItem('n369_last_login_email', form.email.trim().toLowerCase());
      navigate(
        user.email === 'demo.clinica@neuralia369.com'
          ? '/clinic-demo-dashboard'
          : user.role === 'admin'
            ? '/admin'
          : ['doctora', 'enfermera', 'administradora', 'community_manager'].includes(user.role)
            ? '/clinica/panel'
          : user.role === 'mlm_distribuidor'
            ? '/mlm/panel'
          : user.role === 'agencia'
            ? '/agencias/panel'
            : user.role === 'partner' || user.role === 'asociado'
              ? '/partners/panel'
              : '/panel'
      );
    } catch (err) {
      const rawMessage = err?.message || '';
      if (err?.twofaRequired) {
        setTwofa(true);
        setError('Ingresa el código de tu app autenticadora para continuar.');
      } else if (rawMessage.includes('body stream already read') || rawMessage.includes('Failed to execute') || rawMessage.includes('429')) {
        setError('Demasiados intentos en poco tiempo. Espera unos minutos y vuelve a intentarlo.');
      } else {
        setError(rawMessage || 'No se pudo iniciar sesión. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/conectar-google';
  };

  return (
    <>
      <SEO title="Iniciar Sesión — Neuralia369" description="Accede a tu panel" />
      <div className="min-h-screen flex items-center justify-center px-4 py-20" data-testid="login-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-8 text-sm">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>

          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-background" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-bold text-text-heading">{content.title}</h1>
                <p className="text-text-muted text-sm">{content.subtitle}</p>
              </div>
            </div>

            <p className="text-text-muted text-sm mb-5" data-testid="login-mode-hint">{content.hint}</p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-4 text-red-400 text-sm" data-testid="auth-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input type="email" placeholder="Email" required value={form.email} autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                  onChange={e => setForm({...form, email: e.target.value})} className="input-kinesthetic !pl-10" data-testid="auth-email" />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <PasswordInput placeholder="Contraseña" required minLength={6} value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})} inputClassName="input-kinesthetic !pl-10" testId="auth-password" toggleTestId="auth-password-visibility-toggle" />
              </div>

              {twofa && (
                <div className="relative" data-testid="auth-2fa-wrap">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Código 2FA (6 dígitos)" value={form.otp}
                    onChange={e => setForm({...form, otp: e.target.value})} className="input-kinesthetic !pl-10" data-testid="auth-otp" />
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="auth-submit">
                {loading ? (
                  <span className="animate-spin w-5 h-5 border-2 border-background border-t-transparent rounded-full" />
                ) : (
                  <><LogIn className="w-4 h-4" /> Iniciar Sesión</>
                )}
              </button>

              <button type="button" onClick={handleGoogleLogin} className="btn-secondary w-full" data-testid="auth-google-submit">
                <Sparkles className="w-4 h-4" /> Conectar Google desde ruta corta
              </button>

              <p className="text-xs text-text-muted text-center" data-testid="auth-google-hint">
                Si el enlace largo falla, abre la ruta corta y conecta Google desde el Hub embebido al iniciar sesión.
              </p>

              <Link to="/conectar-google" className="text-xs text-primary text-center block hover:underline" data-testid="auth-google-short-link">
                Si quieres una ruta aún más simple, usa esta página corta de acceso Google.
              </Link>
            </form>

            <p className="mt-6 text-center text-text-muted text-xs">
              El acceso es gestionado por el administrador.
              <br />Contacta a <a href="mailto:contacto@neuralia369.com" className="text-primary hover:underline">contacto@neuralia369.com</a> si necesitas una cuenta.
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
}
